import { AppConfigService, AppConfiguration } from 'src/app/core/service/app-config.service';
import { Component, OnInit, ViewEncapsulation, Input, OnDestroy, Output, EventEmitter } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { State } from '@progress/kendo-data-query';
import { GridDataResult, DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { HelperService } from 'src/app/core/service/helper.service';
import { MemberListService } from 'src/app/core/service/grid-data/grid-data-member.service';
import moment from 'moment';
import { Router } from '@angular/router';
import { SettingService } from 'src/app/core/service/setting.service';
import { IntlService } from '@progress/kendo-angular-intl';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { sortBy } from 'lodash';
import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { TranslateService } from '@ngx-translate/core';
import { RegistrationService } from 'src/app/core/service/registration.service';
import { MemberService } from 'src/app/core/service/member.service';
import { Dictionary } from 'src/app/core/model/dictionary';
import { MemberLink } from '../../member/model/member';
import { MessengerService, MessageSubject } from 'src/app/core/service/messenger.service';
import { Subscription } from 'rxjs';
import { CaseService } from 'src/app/core/service/case.service';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';

@Component({
  selector: 'app-member-search',
  templateUrl: './member-search.component.html',
  /*Only way to override for Kendo
  styleUrls: ['./member-search.component.scss'],*/
  encapsulation: ViewEncapsulation.None,
  styles: [`
    .k-maskedtextbox,
    .k-textbox {  width: 100%; }
    .extra-top {  padding-top: 32px !important; }
    .fa-select-member
    {
        color: #cf4526;
        cursor: pointer;
    }`
  ]
})
export class MemberSearchComponent implements OnInit, OnDestroy
{
  @Input() public showHeader: boolean = true;
  @Input() public addHouseholdMember: boolean = false;
  @Input() public enrollChannelId: number = null;
  @Input() public enrollChannelName: string = '';
  @Input() public nextAvailableEnabled: boolean = false;
  @Input() public nextAvailableEnabledNoOpenCaseOnly: boolean = false;
  @Input() public displayTab: boolean = true;
  @Input() public member: any = null;
  @Input() public maxGridHeight = 400;

  @Output() public completeEnrollments: EventEmitter<number[]> = new EventEmitter();

  /**
   * Breadcrumb from route parameters.
   */
  public breadcrumb = 'SEARCH';

  public pageSizes = [5, 10, 25, 50, 100];
  public searchForm: any;
  public gridView: GridDataResult;
  public total: number;
  public genders = [];
  public states = [];
  public allSearchSources = [];
  public searchSourcesFiltered: Dictionary[] = [];

  public minDOB: Date = moment().subtract('120', 'year').toDate();
  public maxDOB: Date = moment().toDate();
  public communityEnabled = false;
  public communityPortalEnabled = false;
  public allowMultipleDashboardEnrollment = false;
  public openSendMethodDialog = false;
  public sendMethodType = '';
  public textBY = '';
  public memberSelected: any;
  public externalSearch = false;
  public showPatientSearchSources = false;
  public confirmAddMember = false;
  public selectedMember: any;
  public linkedMembers: any[] = [];
  public relationshipId: any = null;
  public dateRangeError: string;
  public partOfAHousehold = '';
  public enrollMember = false;
  public selectedRecordKeys: number[] = [];
  public loaded = false;
  public enrollmentReasonForm: UntypedFormGroup;
  public nextEnrollmentText = '';
  public dashboardEnrollmentText = '';
  public creatingCase = false;

  /**
   * Member list Kendo grid state.
   */
  public gridState: State = {
    skip: 0,
    // Initial filter descriptor
    filter: {
      logic: 'and',
      filters: []
    }
  };

  /**
   * Phone number input format mask
   */
  public phoneNumberMask: string;
  public patientLabel = {};
  public subjectIdentifiers: Array<{ displayName: string; id: string }>;

  /**
   * Current search term entered from a comboxbox
   */
  public searchTerm: string;
  public virtualSource: any = { itemHeight: 30 };

  private appConfig: AppConfiguration;
  private channelsSearchSource: any;
  private messagingSubscription: Subscription;

  constructor(
    private fb: UntypedFormBuilder,
    private helperService: HelperService,
    private memberListService: MemberListService,
    private appConfigService: AppConfigService,
    private router: Router,
    private settingService: SettingService,
    public intl: IntlService,
    private messageHandlerService: MessageHandlerService,
    private translate: TranslateService,
    private registrationService: RegistrationService,
    private dictionaryService: DictionaryService,
    private memberService: MemberService,
    private caseService: CaseService,
    private authenticationService: AuthenticationService,
    private messengerService: MessengerService
  )
  {
    this.appConfig = this.appConfigService.getConfig();
    this.translate.get(['NEXT-AVAILABLE-ENROLLMENT-REASON', 'WIZARD-DASHBOARD-ENROLLMENT-TITLE']).subscribe({
      next: (res) =>
      {
        this.nextEnrollmentText = res['NEXT-AVAILABLE-ENROLLMENT-REASON'];
        this.dashboardEnrollmentText = res['WIZARD-DASHBOARD-ENROLLMENT-TITLE'];
      }
    });
  }

  public get preferredName(): UntypedFormControl
  {
    return this.searchForm.get('preferredName');
  }

  public get firstName(): UntypedFormControl
  {
    return this.searchForm.get('firstName');
  }

  public get lastName(): UntypedFormControl
  {
    return this.searchForm.get('lastName');
  }

  public get middleName(): UntypedFormControl
  {
    return this.searchForm.get('middleName');
  }

  public get address(): UntypedFormControl
  {
    return this.searchForm.get('address');
  }

  public get gender(): UntypedFormControl
  {
    return this.searchForm.get('gender');
  }

  public get city(): UntypedFormControl
  {
    return this.searchForm.get('city');
  }

  public get state(): UntypedFormControl
  {
    return this.searchForm.get('state');
  }

  public get postalCode(): UntypedFormControl
  {
    return this.searchForm.get('postalCode');
  }

  public get dateOfBirth(): UntypedFormControl
  {
    return this.searchForm.get('dateOfBirth');
  }

  public get email(): UntypedFormControl
  {
    return this.searchForm.get('email');
  }

  public get cellPhoneNumber(): UntypedFormControl
  {
    return this.searchForm.get('cellPhoneNumber');
  }

  public get homePhoneNumber(): UntypedFormControl
  {
    return this.searchForm.get('homePhoneNumber');
  }

  public get workPhoneNumber(): UntypedFormControl
  {
    return this.searchForm.get('workPhoneNumber');
  }

  public get identifierDictionary(): UntypedFormControl
  {
    return this.searchForm.get('identifierDictionary');
  }

  public get identifier(): UntypedFormControl
  {
    return this.searchForm.get('identifier');
  }

  public get searchSources(): UntypedFormControl
  {
    return this.searchForm.get('searchSources');
  }

  public get nextAvailable(): UntypedFormControl
  {
    return this.searchForm.get('nextAvailable');
  }

  /**
   * Provide loading property from Service.
   */
  public get loading(): boolean
  {
    return this.memberListService.loading;
  }

  public ngOnInit(): void
  {
    this.translate.get(['BY', 'MEMBER-PART-OF-HOUSEHOLD']).subscribe({
      next: (res) =>
      {
        this.textBY = res.BY.toLowerCase();
        this.partOfAHousehold = res['MEMBER-PART-OF-HOUSEHOLD'];
      }
    });

    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.patientLabel = { patientLabel: sessionSetting.patientLabel };
        this.phoneNumberMask = sessionSetting.phoneMask;
        this.communityEnabled = sessionSetting.communityEnabled;
        this.communityPortalEnabled = sessionSetting.communityPortalEnabled;
        this.allowMultipleDashboardEnrollment = sessionSetting.allowMultipleDashboardEnrollment;
      }
    });

    this.dictionaryService.getCachedIdentifiers().subscribe({
      next: (data) =>
      {
        this.subjectIdentifiers = data;
        if (!this.subjectIdentifiers.some(f => f.id === 'CHANNELS'))
        {
          this.subjectIdentifiers.push({
            displayName: this.translate.instant('CHANNELS-RECORD-NUMBER'),
            id: 'CHANNELS'
          });
        }
        this.subjectIdentifiers = sortBy(data.map(d => ({ id: d.id.toString(), displayName: d.displayName })), ['displayName']);
      }
    });

    this.loadLinkedMembers();

    this.messagingSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type && message.type === 'addHouseholdMemberRelationship')
        {
          this.relationshipId = message.data;
        }
      }
    });

    this.enrollMember = this.enrollChannelId && this.enrollChannelId > 0;
    this.selectedRecordKeys = [];
    this.loaded = false;

    // Set search form
    this.searchForm = this.fb.group({
      preferredName: [null],
      firstName: [null],
      lastName: [null],
      middleName: [null],
      gender: [null],
      address: [null],
      city: [null],
      state: [null],
      postalCode: [null],
      dateOfBirth: [null],
      email: [null],
      cellPhoneNumber: [null],
      homePhoneNumber: [null],
      workPhoneNumber: [null],
      identifierDictionary: [null],
      identifier: [null],
      searchSources: [null],
      nextAvailable: [{ value: this.nextAvailableEnabled }],
    });

    this.searchForm.get('identifierDictionary').valueChanges.subscribe({
      next: (identifierDictionary: string) =>
      {
        this.setControlState(identifierDictionary);
      }
    });

    this.helperService.getMemberSearchDataFromMultipleSources().subscribe({
      next: (responseList) =>
      {
        this.genders = responseList[0];
        this.states = responseList[1];
      }
    });

    this.setControlState(this.identifierDictionary.value);

    this.dictionaryService.getCachedPatientSearchSources().subscribe({
      next: (data) =>
      {
        this.allSearchSources = data;
        this.filterSearchSources('');

        this.showPatientSearchSources = false;
        if (!this.addHouseholdMember && !this.enrollMember)
        {
          for (let i = 0; i < this.allSearchSources.length; i++)
          {
            if (this.allSearchSources[i].code !== 'CHANNELS')
            {
              this.showPatientSearchSources = true;
            }
            else
            {
              this.channelsSearchSource = this.allSearchSources[i];
            }
          }
        }

        // Set source default to Channels
        if (this.channelsSearchSource)
        {
          this.searchForm.controls.searchSources.value = [];
          this.searchForm.controls.searchSources.value.push(this.channelsSearchSource);
        }
      }
    });

    this.loaded = !this.nextAvailableEnabled;
    if (this.nextAvailableEnabled)
    {
      this.searchMembers(true, true);
    }
  }

  public ngOnDestroy(): void
  {
    if (this.messagingSubscription !== undefined) { this.messagingSubscription.unsubscribe(); }
  }

  public filterSearchSources(filter: string): void
  {
    this.searchTerm = filter;
    this.searchSourcesFiltered = this.allSearchSources
      .filter(user => user.displayName.toLowerCase().includes(filter.toLowerCase()));
  }

  public clearHidden(): boolean 
  {
    return this.searchDisabled(true);
  }

  /**
   * Check if search button is disabled.
   *
   * @returns boolean Return true is the search is disabled due to lack of search criteria.
   */
  public searchDisabled(clearStateCheck: boolean = false): boolean
  {
    if (this.searchForm !== undefined)
    {
      if (this.showPatientSearchSources && (!this.searchSources.value || this.searchSources.value.length === 0))
      {
        return true;
      }
      else if (this.nextAvailableEnabled && !clearStateCheck) // We allow query with no criteria
      {
        return false;
      }
      else
      {
        return !this.searchForm.valid ||
          (!this.preferredName.value &&
            !this.firstName.value &&
            !this.lastName.value &&
            !this.middleName.value &&
            !this.address.value &&
            !this.city.value &&
            !this.state.value &&
            !this.gender.value &&
            !this.postalCode.value &&
            !this.dateOfBirth.value &&
            !this.email.value &&
            !this.homePhoneNumber.value &&
            !this.identifier.value &&
            !this.identifierDictionary.value &&
            !this.cellPhoneNumber.value &&
            !this.workPhoneNumber.value);
      }
    }
    else
    {
      return true;
    }
  }

  /**
   * Clear all the Member search criteria.
   */
  public clearSearch(): void
  {
    this.preferredName.setValue(null);
    this.firstName.setValue(null);
    this.lastName.setValue(null);
    this.middleName.setValue(null);
    this.address.setValue(null);
    this.city.setValue(null);
    this.state.setValue(null);
    this.gender.setValue(null);
    this.postalCode.setValue(null);
    this.dateOfBirth.setValue(null);
    this.email.setValue(null);
    this.homePhoneNumber.setValue(null);
    this.identifierDictionary.setValue(null);
    this.identifier.setValue(null);
    this.cellPhoneNumber.setValue(null);
    this.workPhoneNumber.setValue(null);
    this.clearGrid();
  }

  public getAdditionalQueryString(showPatientSearchSources: boolean, patientSearchSources: UntypedFormControl): string
  {
    let additionalQueryString = '';
    if (showPatientSearchSources)
    {
      let sources = '';
      for (let i = 0; i < patientSearchSources.value.length; i++)
      {
        if (i === patientSearchSources.value.length - 1)
        {
          sources = sources.concat(patientSearchSources.value[i].code);
        }
        else
        {
          sources = sources.concat(patientSearchSources.value[i].code).concat(',');
        }
      }

      if (sources !== '')
      {
        additionalQueryString = `&sources=${sources}`;
      }
    }
    return additionalQueryString;
  }

  /**
   * Search Members based on criteria
   *
   * @param resetPage Wehn true, the page offset is reset to 0.
   */
  public searchMembers(resetPage = false, selectFirst = false): void
  {
    // Must always reset page offset to 0. Except if changing page.
    if (resetPage)
    {
      this.gridState.skip = 0;
    }

    this.gridState = {
      filter: {
        filters: [], logic: 'and'
      },
      group: [],
      skip: this.gridState.skip !== undefined ? this.gridState.skip : 0,
      sort: this.gridState.sort !== undefined ? this.gridState.sort : [{ field: 'familyName', dir: 'asc' }],
      take: this.gridState.take !== undefined ? this.gridState.take : (this.addHouseholdMember || this.enrollMember) ? 5 : this.appConfig.pageSize
    };

    // Set search criteria
    this.setSearchFilters();

    if (this.showPatientSearchSources)
    {
      this.externalSearch = false;
      for (let i = 0; i < this.searchSources.value.length; i++)
      {
        if (this.searchSources.value[i].code !== 'CHANNELS')
        {
          this.externalSearch = true;
          break;
        }
      }
    }
    else
    {
      this.externalSearch = false;
    }

    // Query Members
    this.memberListService.queryByState(this.gridState, (member: any) =>
    {
      if (member !== null && member.data !== null)
      {
        member.data.forEach(v =>
        {
          if (v.dateOfBirth !== null && v.dateOfBirth !== undefined && v.dateOfBirth.length === 10)
          {
            v.dateOfBirth = new Date(`${v.dateOfBirth}T12:00:00.000`);
          }
          if (v.inviteSentOrRegisteredDate !== null)
          {
            v.inviteSentOrRegisteredDate = new Date(v.inviteSentOrRegisteredDate);
          }
          v.selected = false;
        });
      }
      this.total = member.total;
      this.gridView = { data: member.data, total: member.total };
      if (selectFirst && this.total > 0) 
      {
        this.selectedRecordKeys.push(member.data[0].id);
      }
      this.loaded = true;
    }, this.getAdditionalQueryString(this.showPatientSearchSources, this.searchSources));
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    // this.state = state;
    this.gridState = state;
    this.searchMembers();
  }

  /**
   * Member has been selected.
   *
   * @param dataItem Selected member dataItem.
   */
  public selectMemberHandler(dataItem: any): void
  {
    this.router.navigate([`/patient/${dataItem.id}`]);
  }

  /**
   * Execute search on enter
   *
   * @param e Event
   */
  public keyDown(event: KeyboardEvent): void
  {
    if (event && event.key === 'Enter' && !this.searchDisabled())
    {
      event.preventDefault();
      this.searchMembers(true);
    }
  }

  public navigateToPatient(memberId: number): void
  {
    this.router.navigate([`/patient/${memberId}`]);
  }

  public openMethodDialog(member: any, type: string): void
  {
    this.sendMethodType = type;
    this.memberSelected = member;

    const noCell = !(this.memberSelected.cellPhoneNumber !== null && this.memberSelected.cellPhoneNumber !== '');
    const noEmail = !(this.memberSelected.email !== null && this.memberSelected.email !== '');

    if (noCell && noEmail)
    {
      this.messageHandlerService.displayError('NO-CELL-PHONE-EMAIL-ERROR');
    }
    else if (noEmail && type === 'INVITE')
    {
      this.messageHandlerService.displayError('COMMUNITY-INVITE-EMAIL-REQUIRED');
    }
    else
    {
      if (type === 'INVITE')
      {
        this.registrationService.emailExists(this.memberSelected.email).subscribe({
          next: (response) =>
          {
            if (response)
            {
              this.messageHandlerService.displayError('EMAIL-ALREADY-REGISTERED');
            }
            else
            {
              this.openSendMethodDialog = true;
            }
          }
        });
      }
      else
      {
        this.openSendMethodDialog = true;
      }
    }
  }

  public closeSendMethod(refreshList: boolean): void
  {
    if (refreshList)
    {
      this.memberListService.queryByState(this.state, null,
        this.getAdditionalQueryString(this.showPatientSearchSources, this.searchSources));
    }
    this.openSendMethodDialog = false;
  }

  public continueImportAction(confirm: boolean): void
  {
    this.confirmAddMember = false;
    if (confirm)
    {
      this.performImportMember(this.selectedMember);
    }
  }

  public importMember(dataItem: any): void
  {
    this.confirmAddMember = true;
    this.selectedMember = dataItem;
  }

  public addNewHouseholdMember(dataItem: any): void
  {
    if (dataItem.memberLink.length > 0)
    {
      this.messageHandlerService.displayError('MEMBER-HEAD-OF-HOUSEHOLD');
      return;
    }
    else
    {
      this.memberService.isMemberPartOfAHousehold(dataItem.id).subscribe({
        next: (response) =>
        {
          if (response !== null)
          {
            this.messageHandlerService.displayDirectMessage('error', this.partOfAHousehold.replace('{{headOfHousehold}}', response.fullName));
            return;
          }
          else
          {
            if (this.relationshipId === null)
            {
              this.messageHandlerService.displayWarning('REQUIRED-RELATIONSHIP-ADD-MEMBER');
              return;
            }
            const data: MemberLink = {
              memberId: this.member.id,
              linkMemberId: dataItem.id,
              relationshipId: this.relationshipId
            }
            this.memberService.addHouseholdMember(data).subscribe({
              next: () =>    
              {
                this.messageHandlerService.displaySuccess('HOUSEHOLD-MEMBER-ADDED-SUCCESSFULLY');
                this.linkedMembers.push({ memberId: dataItem.id });
                this.messengerService.sendMessage(new MessageSubject('refreshPatientHouseholdSection'));
              }
            });
          }
        }
      });
    }
  }

  public performImportMember(dataItem: any): void
  {
    const memberToImport: any = {
      familyName: dataItem.familyName,
      givenName: dataItem.givenName,
      middleName: dataItem.middleName,
      fullName: dataItem.fullName,
      prefix: dataItem.prefix,
      suffix: dataItem.suffix,
      dateOfBirth: dataItem.dateOfBirth,
      homePhoneNumber: dataItem.homePhoneNumber,
      cellPhoneNumber: dataItem.cellPhoneNumber,
      workPhoneNumber: dataItem.workPhoneNumber,
      email: dataItem.email,
      gender: dataItem.genderCode,
      maritalStatus: dataItem.maritalStatusCode,
      religiousAffiliation: dataItem.religiousAffiliationCode,
      races: dataItem.races,
      raceEthnicities: dataItem.raceEthnicities,
      spokenLanguages: dataItem.spokenLanguages,
      addressTypeCode: dataItem.addressTypeCode,
      streetLine: dataItem.streetLine,
      city: dataItem.city,
      stateCode: dataItem.stateCode,
      postalCode: dataItem.postalCode,
      county: dataItem.county,
      countryCode: dataItem.countryCode,
      addresses: dataItem.addresses,
      identifiers: dataItem.identifiers,
      source: dataItem.source
    };

    this.memberService.saveMember(memberToImport, true).subscribe({
      next: (data) =>
      {
        this.messageHandlerService.displaySuccess('MEMBER-IMPORT-SUCCESS');
        this.router.navigate([`/patient/${data.id}`]);
      },
      error: () =>
      {
        this.messageHandlerService.displayError('MEMBER-IMPORT-ERROR');
      }
    });
  }

  public getInviteFromOrganizationName(member: any): string
  {
    if (member && member.inviteOrganizationName)
    {
      return `${this.textBY} ${member.inviteOrganizationName}`;
    }
    else
    {
      return '';
    }
  }

  /**
   * Disable the grid when creating cases.
   */
  public rowClass = () => ({ 'k-disabled': this.creatingCase });

  public enrollPatients(): void
  {
    // call service to create case
    let enrollmentCount = 0;
    this.creatingCase = true;
    this.selectedRecordKeys.forEach((memberId: number) =>
    {
      const newCase =
      {
        name: null,
        channel: { id: this.enrollChannelId },
        member: { id: memberId },
        enrollmentReason: this.nextAvailableEnabled ? this.nextEnrollmentText : this.dashboardEnrollmentText,
        manager: { id: this.authenticationService.currentUser.id },
        priority: 50
      };
      this.caseService.createCase(newCase).subscribe({
        next: () =>
        {
          enrollmentCount++;
          if (enrollmentCount === this.selectedRecordKeys.length) 
          {
            this.completeEnrollments.emit();
            this.creatingCase = false;
          }
        },
        error: () =>
        {
          this.messageHandlerService.displayError('CASE-SAVE-ERROR');
          this.completeEnrollments.emit();
        }
      });
    });
  }

  public getSelectedCount(): number
  {
    return this.selectedRecordKeys.length;
  }

  public formValid(): boolean
  {
    if (this.searchForm === undefined)
    {
      return false;
    }
    else
    {
      return this.searchForm.valid;
    }
  }

  public formDirty(): boolean
  {
    return this.searchForm.dirty;
  }

  public isLinkedMember(dataItemId: any): boolean
  {
    return this.linkedMembers.filter(p => p.memberId == dataItemId).length === 1;
  }

  public onSelectedKeysChange(e: any): void
  {
    this.selectedRecordKeys = e;
  }

  private loadLinkedMembers(): void
  {
    this.linkedMembers = [];
    if (this.member !== null && this.member.id > 0)
    {
      this.memberService.getLinkedMember(this.member.id).subscribe({
        next: (linkedMembers) =>
        {
          this.linkedMembers = linkedMembers;
        }
      });
    }
  }

  private setControlState(identifierDictionary: string): void
  {
    const validator = [];
    if (identifierDictionary !== null && identifierDictionary !== undefined && identifierDictionary !== '')
    {
      validator.push(Validators.required);
      this.identifier.enable();
    }
    else
    {
      this.identifier.disable();
      this.identifier.setValue(null);
    }
    this.searchForm.get('identifier').setValidators(validator);
    this.searchForm.get('identifier').updateValueAndValidity();
  }

  /**
   * Clear grid results
   */
  private clearGrid(): void
  {
    this.gridView = { data: [], total: 0 };
  }

  /**
   * Set the Member search filters.
   */
  private setSearchFilters(): void
  {
    if (this.firstName.value)
    {
      this.gridState.filter.filters.push({ field: 'givenName', operator: 'startswith', value: this.firstName.value });
    }

    if (this.preferredName.value)
    {
      this.gridState.filter.filters.push({ field: 'preferredName', operator: 'startswith', value: this.preferredName.value });
    }

    if (this.lastName.value)
    {
      this.gridState.filter.filters.push({ field: 'familyName', operator: 'startswith', value: this.lastName.value });
    }

    if (this.middleName.value)
    {
      this.gridState.filter.filters.push({ field: 'middleName', operator: 'startswith', value: this.middleName.value });
    }

    if (this.gender.value)
    {
      this.gridState.filter.filters.push({ field: 'gender', operator: 'eq', value: this.gender.value.displayName });
    }

    if (this.address.value)
    {
      this.gridState.filter.filters.push({ field: 'streetLine', operator: 'contains', value: this.address.value });
    }

    if (this.state.value)
    {
      this.gridState.filter.filters.push({ field: 'stateCode', operator: 'eq', value: this.state.value.code });
    }

    if (this.city.value)
    {
      this.gridState.filter.filters.push({ field: 'city', operator: 'startswith', value: this.city.value });
    }

    if (this.postalCode.value)
    {
      this.gridState.filter.filters.push({ field: 'postalCode', operator: 'startswith', value: this.postalCode.value });
    }

    if (this.dateOfBirth.value)
    {
      this.gridState.filter.filters.push({ field: 'dateOfBirth', operator: 'eq', value: this.dateOfBirth.value });
    }

    if (this.email.value)
    {
      this.gridState.filter.filters.push({ field: 'email', operator: 'startswith', value: this.email.value });
    }

    if (this.nextAvailableEnabled || this.nextAvailableEnabledNoOpenCaseOnly)
    {
      this.gridState.filter.filters.push({ field: 'nextAvailable', operator: 'eq', value: true });
      if (this.nextAvailableEnabledNoOpenCaseOnly) 
      {
        this.gridState.filter.filters.push({ field: 'nextAvailableEnabledNoOpenCaseOnly', operator: 'eq', value: true });
      }
    }

    if (this.enrollMember)
    {
      this.gridState.filter.filters.push({ field: 'enrollChannelId', operator: 'eq', value: this.enrollChannelId });
    }

    if (this.stripPhoneMark(this.cellPhoneNumber.value))
    {
      this.gridState.filter.filters.push({
        field: 'cellPhoneNumber', operator: 'eq',
        value: this.stripPhoneMark(this.cellPhoneNumber.value)
      });
    }

    if (this.stripPhoneMark(this.homePhoneNumber.value))
    {
      this.gridState.filter.filters.push({
        field: 'homePhoneNumber', operator: 'eq',
        value: this.stripPhoneMark(this.homePhoneNumber.value)
      });
    }

    if (this.stripPhoneMark(this.workPhoneNumber.value))
    {
      this.gridState.filter.filters.push({
        field: 'workPhoneNumber', operator: 'eq',
        value: this.stripPhoneMark(this.workPhoneNumber.value)
      });
    }

    if (this.identifierDictionary.value && this.identifier.value)
    {
      this.gridState.filter.filters.push({ field: 'identifierDictionary', operator: 'eq', value: this.identifierDictionary.value });
      this.gridState.filter.filters.push({ field: 'identifier', operator: 'eq', value: this.identifier.value });
    }
  }

  private stripPhoneMark(value: string): string
  {
    if (value !== null)
    {
      return value.replace('(', '').replace(')', '').replace('-', '').replace(/ /g, '');
    }
    else
    {
      return value;
    }
  }
}
